import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, useClients, useTokens, ApiTokenCreated } from '../lib/api';

// Scopes that can be assigned to an API token. `admin` and `secrets` are
// intentionally absent — those are reserved for operator UI sessions. A
// token holder cannot rotate GA credentials, read mailbox passwords,
// teardown a tenant, or run a domain swap, so issuing a token to a
// customer never gives them the ability to take their tenant elsewhere.
const SCOPES = [
  { key: 'read',  label: 'read',  desc: 'list/get tenants, clients, mailboxes, logs, queue status' },
  { key: 'write', label: 'write', desc: 'retry safe phases (mailboxes/permissions/oauth/etc.), reassign client' },
];

export default function Tokens() {
  const { data: tokens, refetch } = useTokens();
  const [showCreate, setShowCreate] = useState(false);
  const [createdToken, setCreatedToken] = useState<ApiTokenCreated | null>(null);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h1 className="page-h1 text-xl">API tokens</h1>
          <p className="page-sub mt-1">
            Bearer tokens for the public REST API + MCP server. Use as{' '}
            <code className="font-mono text-ink">Authorization: Bearer ie_…</code>.
            Cleartext is shown <strong>once</strong> on creation. Tokens are limited to{' '}
            <code className="font-mono text-ink">read</code> and{' '}
            <code className="font-mono text-ink">write</code>; operator-only actions are
            never available to tokens.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary btn-lg">+ New token</button>
      </header>

      <div className="card overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Client scope</th>
              <th>Scopes</th>
              <th>Created</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(tokens || []).map(t => (
              <tr key={t.id}>
                <td className="font-medium">{t.name}</td>
                <td className="text-xs">
                  {t.client_id
                    ? <span className="pill-dim font-mono">{t.client_id.slice(0,8)}…</span>
                    : <span className="text-dim">global</span>}
                </td>
                <td>
                  <div className="flex gap-1">
                    {t.scopes.map(s => (
                      <span key={s} className="pill-dim font-mono">{s}</span>
                    ))}
                  </div>
                </td>
                <td className="text-xs text-dim tabular-nums">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="text-xs text-dim tabular-nums">
                  {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : <span className="opacity-60">never</span>}
                </td>
                <td className="text-right">
                  <button
                    onClick={async () => {
                      if (!confirm(`Revoke "${t.name}"? Apps using this token will stop working.`)) return;
                      await api.revokeToken(t.id);
                      refetch();
                    }}
                    className="btn-danger btn-sm"
                  >Revoke</button>
                </td>
              </tr>
            ))}
            {tokens && tokens.length === 0 && (
              <tr><td colSpan={6}>
                <div className="empty">
                  <div className="text-base">No tokens yet</div>
                  <div className="opacity-70">Create one to integrate with external tools or the MCP server.</div>
                  <button onClick={() => setShowCreate(true)} className="btn-primary mt-4">+ New token</button>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(t) => { setShowCreate(false); setCreatedToken(t); refetch(); }}
        />
      )}
      {createdToken && <NewTokenDialog token={createdToken} onClose={() => setCreatedToken(null)} />}
    </div>
  );
}


function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (t: ApiTokenCreated) => void }) {
  const { data: clients } = useClients();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');     // '' = global
  const [scopes, setScopes] = useState<string[]>(['read']);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(scope: string) {
    setScopes(s => s.includes(scope) ? s.filter(x => x !== scope) : [...s, scope]);
  }

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const t = await api.createToken({
        name, client_id: clientId || null, scopes,
      });
      qc.invalidateQueries({ queryKey: ['tokens'] });
      onCreated(t);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  return (
    <Modal title="New API token" onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <div className="text-xs text-dim mb-1">Name (for your reference)</div>
          <input className="input" placeholder="zapier integration"
            value={name} onChange={e => setName(e.target.value)} />
        </label>
        <label className="block">
          <div className="text-xs text-dim mb-1">Client scope</div>
          <select className="input" value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">Global (all clients)</option>
            {(clients || []).map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.slug})</option>
            ))}
          </select>
        </label>
        <div>
          <div className="text-xs text-dim mb-2">Scopes</div>
          <div className="space-y-2">
            {SCOPES.map(s => (
              <label key={s.key} className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={scopes.includes(s.key)} onChange={() => toggle(s.key)} className="mt-1" />
                <div className="text-xs">
                  <div><code className="font-mono">{s.label}</code></div>
                  <div className="text-dim">{s.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        {err && <div className="text-err text-xs">{err}</div>}
        <button disabled={!name || scopes.length === 0 || busy} onClick={submit} className="btn-primary w-full">
          Create token
        </button>
      </div>
    </Modal>
  );
}


function NewTokenDialog({ token, onClose }: { token: ApiTokenCreated; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal title="New token created" onClose={onClose}>
      <div className="space-y-3">
        <div className="text-xs text-warn bg-warn/10 border border-warn/30 rounded p-2">
          Copy this token now — it will not be shown again. The hash is stored server-side; we cannot recover the cleartext.
        </div>
        <div className="bg-bg border border-line rounded p-2 font-mono text-xs break-all">
          {token.token}
        </div>
        <button
          onClick={async () => {
            await navigator.clipboard.writeText(token.token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="btn-primary w-full"
        >{copied ? '✓ copied' : 'Copy to clipboard'}</button>
        <div className="text-xs text-dim">
          <div><strong>Name:</strong> {token.name}</div>
          <div><strong>Scopes:</strong> <code className="font-mono">{token.scopes.join(', ')}</code></div>
          <div><strong>Client:</strong> {token.client_id || 'global'}</div>
        </div>
        <button onClick={onClose} className="btn w-full">I've saved it</button>
      </div>
    </Modal>
  );
}


function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0d0d12] border border-line rounded-lg shadow-modal animate-slide-up w-full max-w-[500px]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-5 py-4 border-b border-line">
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          <button
            onClick={onClose}
            className="text-dim hover:text-ink transition w-7 h-7 grid place-items-center rounded hover:bg-white/[0.05]"
            aria-label="Close"
          >✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
